

































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import { ModalSize } from '@getaccept/lib-ui';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import { UrlHelper } from '@getaccept/lib-shared/src/helpers/url.helper';
import type { Entity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import VerifySignature from '@getaccept/lib-shared/src/signing/components/VerifySignature.vue';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import type { Channel } from '@getaccept/pusher';
import { SigningMergeTagHelper } from '@getaccept/lib-shared/src/helpers/signing-merge-tag.helper';
import { EmailHelper } from '../../../helpers/email.helper';
import type { Sign } from '../../types/sign';
import type { Signature } from '../../types/signature';
import type { SigningStep } from '../../types/signing-step';
import { SigningStep as ESigningStep } from '../../types/enums/signing-step';
import type { Qna } from '../../types/qna';
import type { Sms } from '../../types/sms';
import type { AfterSign as IAfterSign } from '../../types/after-sign';
import type { AfterSignStep } from '../../types/enums/after-sign-step';
import { SigningAction } from '../../store/signing.constants';
import { Session } from '../../../session/store/session.constants';
import type { SessionState } from '../../../session/store/session.types';
import EidEasyModal from '../../../eid-easy/EidEasyModal.vue';
import { abortApollo } from '../../../apollo';
import SignConfirmation from './SignConfirmation.vue';
import SignEvidence from './SignEvidence.vue';
import VerifyClick from './VerifyClick.vue';
import VerifySms from './VerifySms.vue';
import VerifyQna from './VerifyQna.vue';
import Eid from './Eid.vue';
import AfterSign from './signup-after-sign/AfterSign.vue';
import NoSignupOption from './signup-after-sign/NoSignupOption.vue';

@Component({
  components: {
    Eid,
    VerifySignature,
    VerifyQna,
    VerifySms,
    VerifyClick,
    SignEvidence,
    SignConfirmation,
    AfterSign,
    EidEasyModal,
    NoSignupOption,
  },
  data: () => ({
    ESigningStep,
    RecipientStatus,
    ModalSize,
  }),
})
export default class SignModal extends Vue {
  @Prop() document!: SigningPageDocument;
  @Prop() entity!: Entity;
  @Prop() sender!: SigningPageUser;
  @Prop() recipient!: SigningPageRecipient;
  @Prop() authId!: string;
  @Prop() step!: SigningStep;
  @Prop() signButtonText?: string;
  @Prop() reviewPending: boolean;
  @Prop() shouldPopStripe: boolean;
  @Prop() recipientPusherChannel!: Channel;

  @Action(SigningAction.CheckVerificationStep, { namespace: 'signing' })
  checkVerificationStep: any;
  @Action(SigningAction.VerifyQna, { namespace: 'signing' }) verifyQna: any;
  @Action(SigningAction.SendSmsCode, { namespace: 'signing' }) sendSmsCode: any;
  @Action(SigningAction.VerifySms, { namespace: 'signing' }) verifySms: any;
  @Action(SigningAction.VerifySignature, { namespace: 'signing' }) verifySignature: any;
  @Action(SigningAction.SignDocument, { namespace: 'signing' }) signDocument: any;
  @Action(SigningAction.SendFeedback, { namespace: 'signing' }) sendFeedback: any;
  @Action(SigningAction.CreateSignup, { namespace: 'signing' }) createSignup: any;
  @Action(SigningAction.SetAfterSignStep, { namespace: 'signing' }) setAfterSignStep: any;

  @State('loading', { namespace: 'signing' }) loading!: boolean;
  @State('qna', { namespace: 'signing' }) qna!: Qna;
  @State('sms', { namespace: 'signing' }) sms!: Sms;
  @State('signature', { namespace: 'signing' }) signature!: Signature;
  @State('sign', { namespace: 'signing' }) sign!: Sign;
  @State('afterSign', { namespace: 'signing' }) afterSign: IAfterSign;
  @State('initials', { namespace: 'signing' }) initials: string;

  @Session.State((state: SessionState) => state.token) token: { jwt: string; exp: number };

  @Watch('recipient.status')
  handleWatchRecipientStatus(newValue: RecipientStatus) {
    const shouldRedirect = newValue === RecipientStatus.Signed && !!this.entity.signRedirect;
    if (shouldRedirect) {
      setTimeout(() => {
        let { signRedirect } = this.entity;
        if (SigningMergeTagHelper.findTags(signRedirect)) {
          signRedirect = SigningMergeTagHelper.replaceTags(signRedirect, {
            recipient: this.recipient,
            document: this.document,
            entity: this.entity,
          });
        }
        const url = UrlHelper.getHttpsUrl(signRedirect);
        abortApollo();
        window.open(url, '_self');
      }, 300);
    }
  }

  hasSignedBankID = false;
  currentCampaign = 'v3/module/yes';

  get showConfirmWindow(): boolean {
    return !this.sign.isVerifying && this.recipient.status === RecipientStatus.Signed;
  }

  get qesEnabled(): boolean {
    return this.document?.settings?.qualifiedElectronicSignature;
  }

  get showClickToSign(): boolean {
    return (
      this.step.type === ESigningStep.Click &&
      !this.sign.isVerifying &&
      this.recipient.status !== RecipientStatus.Signed
    );
  }

  get showConfirmation(): boolean {
    return this.recipient.status === RecipientStatus.Signed && !this.sign.isVerifying;
  }

  get showEid(): boolean {
    return this.step.type === ESigningStep.Eid && !this.hasSignedBankID;
  }

  get showSignupAfterSign(): boolean {
    if (this.entity.signRedirect) {
      return false;
    }
    if (!this.sender.email && !this.recipient.email) {
      return false;
    }
    return (
      EmailHelper.getEmailDomain(this.sender.email) !==
      EmailHelper.getEmailDomain(this.recipient.email)
    );
  }

  get signText(): string {
    return this.$t('confirm-signature', {
      'recipient-name': this.recipientName,
      'document-name': this.document.name,
    }).toString();
  }

  get recipientName(): string {
    if (this.recipient.fullName) {
      return this.recipient.fullName;
    }
    if (this.recipient.firstName || this.recipient.lastName) {
      return `${this.recipient.firstName} ${this.recipient.lastName}`;
    }

    return this.recipient.email;
  }

  handleVerifySignature({ signatureBase30, signatureBase64 }) {
    if (this.reviewPending) {
      this.closeAndShowReviewModal();
      return;
    }
    this.verifySignature({ signatureBase30, signatureBase64 });
  }

  handleSignDocumentEid() {
    if (this.reviewPending) {
      this.closeAndShowReviewModal();
      return;
    }
    this.hasSignedBankID = true;
    if (this.document.isSigningInitials) {
      this.checkVerificationStep();
    } else {
      this.signDocument();
    }
  }

  handleVerifyClick(initials: string) {
    if (this.reviewPending) {
      this.closeAndShowReviewModal();
      return;
    }
    this.signDocument(initials);
  }

  closeAndShowReviewModal() {
    this.handleClose();
    this.$emit('showReviewModal');
  }

  handleClose(): void {
    if (this.reviewPending) {
      this.$emit('showReviewModal');
    }
    if (this.showEid) {
      this.$emit('cancelBankId');
    }
    this.$emit('close');
  }

  handleSendFeedback(feedback: string, email: string): void {
    this.sendFeedback({ feedback, email });
  }

  handleSignup(email: string): void {
    this.createSignup({ email, campaign: this.currentCampaign });
  }

  handleAfterSignStep(step: AfterSignStep) {
    this.setAfterSignStep(step);
  }

  mounted() {
    if (!this.showEid) {
      this.checkVerificationStep();
    }
  }
}
